/**
 * Readiness as one slim hint line (missions vision): "Too large: Split story", never a card with
 * a paragraph. Several findings fold into "3 things to make it ready", one click opens them.
 * Grey: readiness is a to-do, not an alarm. The finding's message is the line's tooltip.
 */
import { ChevronRight, CircleDashed } from '@orgus/ui/icons';
import { useState } from 'react';
import { QUALITY_LABEL } from '@orgus/protocol';
import type { QualityIssue } from '@orgus/protocol';
import { Button, Pressable, Tooltip, cn, formatInt } from '@orgus/ui';

function Line({ q, onFix }: { q: QualityIssue; onFix: (q: QualityIssue) => void }) {
  return (
    <div className="flex h-control-sm min-w-0 items-center gap-2">
      <Tooltip content={q.message}>
        <span tabIndex={0} className="min-w-0 truncate text-ui text-fg-muted">
          {QUALITY_LABEL[q.code]}
        </span>
      </Tooltip>
      {q.fix && q.fixLabel && (
        <Button variant="ghost" size="sm" className="-ml-1 shrink-0 text-accent-text" onClick={() => onFix(q)}>
          {q.fixLabel}
        </Button>
      )}
    </div>
  );
}

export function ReadinessHint({ issues, onFix, className }: { issues: readonly QualityIssue[]; onFix: (q: QualityIssue) => void; className?: string }) {
  const [open, setOpen] = useState(false);
  if (!issues.length) return null;
  if (issues.length === 1) {
    return (
      <div className={cn('flex min-w-0 items-center gap-2', className)} aria-label="To make it ready">
        <CircleDashed className="size-4 shrink-0 text-fg-subtle" aria-hidden />
        <Line q={issues[0]!} onFix={onFix} />
      </div>
    );
  }
  return (
    <div className={cn('flex min-w-0 flex-col', className)} aria-label="To make it ready">
      <Pressable aria-expanded={open} onClick={() => setOpen((v) => !v)} className="-ml-1 flex h-control-sm w-auto items-center gap-2 rounded-control px-1 text-ui text-fg-muted hover:bg-hover hover:text-fg">
        <CircleDashed className="size-4 shrink-0 text-fg-subtle" aria-hidden />
        <span>{formatInt(issues.length)} things to make it ready</span>
        <ChevronRight className={cn('size-3 shrink-0 text-fg-subtle transition-transform duration-(--duration-fast)', open && 'rotate-90')} aria-hidden />
      </Pressable>
      {open && (
        <div className="flex flex-col pl-6">
          {issues.map((q, i) => (
            <Line key={`${q.code}-${q.criterionId ?? i}`} q={q} onFix={onFix} />
          ))}
        </div>
      )}
    </div>
  );
}
